import qs from 'qs'
import * as Sentry from '@sentry/browser'
import Cookies from 'js-cookie'
import {getClient, getConnection, getLang} from 'utils/stringUtils'
import {FORM_STATE} from 'const/googleAnalytics'
import {getDeployEnv} from 'utils/envUtils'
import {getBrand} from 'utils/logger'
import {getOnlyNewsletterTitles, getNewsletterChanges} from 'utils/newsletters'
import {BRANDS} from 'const'

const lang = getLang(window.location.search) || 'de'

class GoogleAnalyticsService {
  static pushToGTMDataLayer = event => {
    try {
      if (!Array.isArray(window.dataLayer)) {
        window.dataLayer = []
      }
      window.dataLayer.push(event)
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  static setExternalTrackingCookie = () => {
    const oldValue = Cookies.get('_ext_ga')
    const queryString = qs.parse(window.location.search, {ignoreQueryPrefix: true})
    const {gacid, ga4_sid: ga4sid, ga4_value: ga4value} = queryString

    if (!!gacid && (!oldValue || oldValue !== gacid)) {
      Cookies.set('_ext_ga', gacid, {expires: 100, domain: process.env.REACT_APP_OVERRIDE_DOMAIN})
      Cookies.set('_ga', gacid, {expires: 100, domain: process.env.REACT_APP_OVERRIDE_DOMAIN})
    }

    if (!!ga4sid && !!ga4value) {
      Cookies.set(`_ga_${ga4sid}`, ga4value, {
        expires: 100,
        domain: process.env.REACT_APP_OVERRIDE_DOMAIN,
      })
    }
  }

  static trackVerificationFlow2 = () => {
    this.pushToGTMDataLayer({event: 'verificationFlow2'})
  }

  static trackEmailNext = () => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_email_next',
      event_name: 'email_next',
      email_next: {
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackDeleteAccountRequest = () => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_delete_account_request',
      event_name: 'delete_account_request',
      delete_account_request: {
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackDeleteAccountConfirm = (userId, isCompleteAccountDeletion) => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_delete_account_confirm',
      event_name: 'delete_account_confirm',
      user_id: userId,
      delete_account_confirm: {
        media_title: brand,
        language: lang,
        is_complete_account_deletion: isCompleteAccountDeletion,
      },
    })
  }

  static trackDeleteAccountCancel = userId => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_delete_account_cancel',
      event_name: 'delete_account_cancel',
      user_id: userId,
      delete_account_cancel: {
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackDownloadAccountData = userId => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_download_data',
      event_name: 'download_data',
      user_id: userId,
      download_data: {
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackForgotPassword = () => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_forgot_password',
      event_name: 'forgot_password',
      forgot_password: {
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackResetPassword = () => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_reset_password',
      event_name: 'reset_password',
      reset_password: {
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackChangePassword = userId => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_change_password',
      event_name: 'change_password',
      user_id: userId,
      change_password: {
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackAddAddress = userId => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_add_address',
      event_name: 'add_address',
      user_id: userId,
      add_address: {
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackEditAddress = userId => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_edit_address',
      event_name: 'edit_address',
      user_id: userId,
      edit_address: {
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackSaveAddress = userId => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_save_address',
      event_name: 'save_address',
      user_id: userId,
      save_address: {
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackDeleteAddress = userId => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_delete_address',
      event_name: 'delete_address',
      user_id: userId,
      delete_address: {
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackGeneralNavigation = (area, item) => {
    this.pushToGTMDataLayer({
      event: 'gaEvent_navigation',
      event_name: 'navigation',
      navigation: {
        navigation_area: area,
        navigation_item: item,
      },
    })
  }

  static trackClickContact = () => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent',
      event_name: 'contact',
      contact: {
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackContactFormSubmit = topic => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent',
      event_name: 'contact_form',
      contact_form: {
        media_title: brand,
        language: lang,
        topic,
      },
    })
  }

  static trackMoreInfoLink = () => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_more_information',
      event_name: 'more_information',
      more_information: {
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackSavePasswordSuccess = () => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_save_password_success',
      event_name: 'save_password_success',
      save_password_success: {
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackSavePasswordFailure = errorMessage => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_save_password_fail',
      event_name: 'save_password_fail',
      save_password_fail: {
        media_title: brand,
        language: lang,
        error_message: errorMessage,
      },
    })
  }

  static trackEmailForceStart = () => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_email_force',
      event_name: 'email_force',
      email_force: {
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackOriginalLocation = () => {
    this.pushToGTMDataLayer({
      originalLocation: `${document.location.protocol}//${document.location.hostname}${document.location.pathname}${document.location.search}`,
    })
  }

  static trackEmailVerificationFail = () => {
    this.pushToGTMDataLayer({
      event: 'gaEvent_email_verification_fail',
      event_name: 'email_verification_fail',
      email_verification_fail: {
        media_title: '{media_title}',
        language: '{language}',
      },
    })
  }

  static trackEmailVerificationSuccess = () => {
    this.pushToGTMDataLayer({
      event: 'gaEvent_email_verification_success',
      event_name: 'email_verification_success',
      email_verification_success: {
        media_title: '{media_title}',
        language: '{language}',
      },
    })
  }

  static trackLazyMigration = () => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_lazy_migration',
      event_name: 'lazy_migration',
      lazy_migration: {
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackDataMigration = () => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_data_migration',
      event_name: 'data_migration',
      data_migration: {
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackLoginStart = () => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_login_start',
      event_name: 'login_start',
      login_start: {
        method: GoogleAnalyticsService.LOGIN_METHODS.NATIVE,
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackLoginSuccess = userId => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_login',
      event_name: 'login',
      user_id: userId,
      login: {
        method: GoogleAnalyticsService.LOGIN_METHODS.NATIVE,
        form_state: FORM_STATE.success,
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackLoginError = (loginMethod, errorMessage) => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_login',
      event_name: 'login',
      login: {
        method: loginMethod,
        form_state: FORM_STATE.error,
        media_title: brand,
        language: lang,
        error_message: errorMessage,
      },
    })
  }

  static trackRegistrationStart = () => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_registration_start',
      event_name: 'registration_start',
      registration_start: {
        method: GoogleAnalyticsService.LOGIN_METHODS.NATIVE,
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackRegistrationFail = errorMessage => {
    const brand = getBrand()
    const isOneLog = brand === BRANDS.onelog
    const eventName = `sign_up${isOneLog ? '_onelog' : ''}`
    const method = getConnection(window.location.search) || GoogleAnalyticsService.LOGIN_METHODS.SEAMLESS
    this.pushToGTMDataLayer({
      event: `gaEvent_${eventName}`,
      event_name: eventName,
      [eventName]: {
        method,
        form_state: FORM_STATE.error,
        media_title: brand,
        language: lang,
        error_message: errorMessage,
      },
    })
  }

  static trackRegistrationContinue = () => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_registration_continue',
      event_name: 'registration_continue',
      registration_continue: {
        method: GoogleAnalyticsService.LOGIN_METHODS.NATIVE,
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackRegistrationEmailSendAgain = () => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_registration_email_sendagain',
      event_name: 'registration_email_sendagain',
      registration_email_sendagain: {
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackRegistrationEmailIncorrect = () => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_registration_email_incorrect',
      event_name: 'registration_email_incorrect',
      registration_email_incorrect: {
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackRegistrationGotoLogin = () => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_registration_goto_login',
      event_name: 'registration_goto_login',
      registration_goto_login: {
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackAccountActivation = () => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_account_activation',
      event_name: 'account_activation',
      account_activation: {
        method: GoogleAnalyticsService.LOGIN_METHODS.NATIVE,
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackAccountActivationSuccess = () => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_account_activation_success',
      event_name: 'account_activation_success',
      account_activation_success: {
        method: GoogleAnalyticsService.LOGIN_METHODS.NATIVE,
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackAccountActivationFail = errorMessage => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_account_activation_fail',
      event_name: 'account_activation_fail',
      account_activation_fail: {
        method: GoogleAnalyticsService.LOGIN_METHODS.NATIVE,
        media_title: brand,
        language: lang,
        error_message: errorMessage,
      },
    })
  }

  static trackLoginOptions = loginMethod => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_login_options',
      event_name: 'login_options',
      login_options: {
        method: loginMethod,
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackLogout = userId => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_logout',
      event_name: 'logout',
      user_id: userId,
      logout: {
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackLoginTouchId = () => {
    const brand = getBrand()
    this.pushToGTMDataLayer({
      event: 'gaEvent_login_touchid',
      event_name: 'login_touchid',
      login_touchid: {
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackSignUp = ({isOneLog, method = GoogleAnalyticsService.LOGIN_METHODS.SEAMLESS}) => {
    const brand = getBrand()
    const eventName = `sign_up${isOneLog ? '_onelog' : ''}`
    this.pushToGTMDataLayer({
      event: `gaEvent_${eventName}`,
      event_name: eventName,
      [eventName]: {
        method,
        form_state: FORM_STATE.success,
        media_title: brand,
        language: lang,
      },
    })
  }

  static trackWebauthSetupEvents = event => {
    const eventLabels = {
      [GoogleAnalyticsService.WEBAUTH_EVENTS.ACTIVATE]: 'Active',
      [GoogleAnalyticsService.WEBAUTH_EVENTS.POSTPONE]: 'Ask again',
      [GoogleAnalyticsService.WEBAUTH_EVENTS.REJECT]: 'Reject',
    }

    if (eventLabels[event]) {
      const brand = getBrand()
      const eventName = `login_webauthn_${event}`

      this.pushToGTMDataLayer({
        event: `gaEvent_${eventName}`,
        event_name: eventName,
        [eventName]: {
          media_title: brand,
          language: lang,
        },
      })
    }
  }

  static trackVirtualPageView = (location, userId) => {
    const brand = getBrand()
    let pagePath = location.pathname
    const clientId = getClient(window.location.search)

    if (pagePath === '/register') {
      const qsParams = qs.parse(location.search, {ignoreQueryPrefix: true})
      const hasEmail = Boolean(qsParams.email)
      const isOnepager = qsParams.is_onepager === 'true'
      const isLazyMigrationOnePager = qsParams.is_lazy_migration_one_pager === 'true'
      pagePath += `?email_prefilled=${hasEmail}&is_onepager=${isOnepager}&is_lazy_migration_one_pager=${isLazyMigrationOnePager}`
    }

    this.pushToGTMDataLayer({
      event: 'virtPageView',
      page_path: pagePath,
      page_type: '--',
      page_title: document.title,
      ga_client_id: Cookies.get('_ga'),
      ...(userId && {user_id: userId}),
      ...(clientId && {client_id: clientId}),
      media_title: brand,
      language: lang,
      environment: getDeployEnv(),
      first_time_signup: '--',
      workflow_info: '--',
      migration: '--',
    })
  }

  static trackAdditionalInformation = (method, options = {}) => {
    const brand = getBrand()
    const {migration, firstTimeSignupOneLog, firstTimeSignupBrand, prefilledPersonalInformation, errorMessage} = options

    this.pushToGTMDataLayer({
      event: 'gaEvent_additional_information',
      event_name: 'additional_information',
      additional_information: {
        method,
        media_title: brand,
        language: lang,
        error_message: errorMessage,
        migration,
        first_time_signup_onelog: Boolean(firstTimeSignupOneLog),
        first_time_signup_brand: Boolean(firstTimeSignupBrand),
        prefilled_personal_information: Boolean(prefilledPersonalInformation),
      },
    })
  }

  static trackNewsletterSubscriptionSuccess = (method, newsletterList) => {
    this.pushToGTMDataLayer({
      event: 'gaEvent_subscribe_newsletter',
      event_name: 'subscribe_newsletter',
      subscribe_newsletter: {
        method,
        newsletter_list: getOnlyNewsletterTitles(newsletterList),
      },
    })
  }

  static trackNewsletterUnsubscriptionSuccess = (method, newsletterList) => {
    this.pushToGTMDataLayer({
      event: 'gaEvent_unsubscribe_newsletter',
      event_name: 'unsubscribe_newsletter',
      unsubscribe_newsletter: {
        method,
        newsletter_list: getOnlyNewsletterTitles(newsletterList),
      },
    })
  }

  static checkSubscriptionsForGA = (brand, newNewsletters, initialNewsletters) => {
    // Supports all different newsletters models: { ... }, [], { [brand]: {} }
    const initialBrandNewsletters = initialNewsletters?.[brand] || initialNewsletters
    const newBrandNewsletters = newNewsletters?.[brand] || newNewsletters
    const isFromProfile = !!initialBrandNewsletters && Object.keys(initialBrandNewsletters).length > 0

    if (newBrandNewsletters && !Array.isArray(newBrandNewsletters)) {
      const {subscriptionList, unsubscriptionList} = getNewsletterChanges(newBrandNewsletters, initialBrandNewsletters)
      const method = isFromProfile
        ? GoogleAnalyticsService.NEWSLETTER_METHODS.PROFILE
        : GoogleAnalyticsService.NEWSLETTER_METHODS.SIGN_UP

      if (subscriptionList.length > 0) {
        GoogleAnalyticsService.trackNewsletterSubscriptionSuccess(method, subscriptionList)
      }
      if (isFromProfile && unsubscriptionList.length > 0) {
        GoogleAnalyticsService.trackNewsletterUnsubscriptionSuccess(method, unsubscriptionList)
      }
    }
  }
}

GoogleAnalyticsService.LOGIN_METHODS = {
  SWISSID: 'swissid',
  MICROSOFT: 'microsoft',
  FACEBOOK: 'facebook',
  GOOGLE: 'google',
  APPLE: 'apple',
  NATIVE: 'native',
  SEAMLESS: 'seamless',
}

GoogleAnalyticsService.NEWSLETTER_METHODS = {
  PROFILE: 'profile tick box',
  SIGN_UP: 'registration form tick box',
}

GoogleAnalyticsService.WEBAUTH_EVENTS = {ACTIVATE: 'activate', POSTPONE: 'postpone', REJECT: 'reject'}

export default GoogleAnalyticsService
